import { useState, useRef, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/* Kasara voice: TTS playback of replies + mic recording -> Whisper transcription. */
export const useVoice = (lang) => {
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem('daakyi_voice') === 'on'; } catch { return false; }
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recState, setRecState] = useState('idle'); // idle | recording | transcribing
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const toggleVoice = useCallback(() => {
    setVoiceOn((v) => {
      const next = !v;
      try { localStorage.setItem('daakyi_voice', next ? 'on' : 'off'); } catch { /* private mode */ }
      if (!next) stopSpeaking();
      return next;
    });
  }, [stopSpeaking]);

  const speak = useCallback(async (text) => {
    const clean = (text || '').trim();
    if (!clean) return;
    try {
      const res = await fetch(`${API}/api/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean.slice(0, 1500), lang })
      });
      if (!res.ok) return;
      const { audio } = await res.json();
      stopSpeaking();
      const el = new Audio(`data:audio/mp3;base64,${audio}`);
      audioRef.current = el;
      setIsSpeaking(true);
      el.onended = () => setIsSpeaking(false);
      el.onerror = () => setIsSpeaking(false);
      await el.play();
    } catch {
      setIsSpeaking(false);
    }
  }, [lang, stopSpeaking]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const startRecording = useCallback(async (onTranscript, onError) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      const maxTimer = setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, 60000);
      rec.onstop = async () => {
        clearTimeout(maxTimer);
        stream.getTracks().forEach((t) => t.stop());
        setRecState('transcribing');
        try {
          const blob = new Blob(chunksRef.current, { type: mime });
          const b64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const res = await fetch(`${API}/api/voice/stt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ audio_base64: b64, mime, lang })
          });
          if (res.ok) {
            const { text } = await res.json();
            if (text) onTranscript(text);
            else onError && onError('empty');
          } else {
            onError && onError('stt');
          }
        } catch {
          onError && onError('stt');
        }
        setRecState('idle');
      };
      recorderRef.current = rec;
      rec.start();
      setRecState('recording');
    } catch {
      setRecState('idle');
      onError && onError('mic');
    }
  }, [lang]);

  useEffect(() => () => {
    stopSpeaking();
    if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
  }, [stopSpeaking]);

  return { voiceOn, toggleVoice, speak, isSpeaking, stopSpeaking, recState, startRecording, stopRecording };
};
